import React, { useMemo } from "react";
import { Page, Document, View, Image, Text } from "@react-pdf/renderer";
import { makePdfStyleSheet } from "./styles";
import moment from "moment";
import { CertidaoNegativaDebitosDto } from "core/features/relatorios/typings";
import { makeImageSrcObject } from "components/PDF/util";
import DadosTenant from "./components/DadosTenant";
import DadosAdministradora from "./components/DadosAdministradora";
import PDFAssinaturas from "ui/components/PDF/components/Assinaturas";
import { TenantTipoHabitacao } from "core/resources/enums/tenant";

interface PDFCertidaoNegativaDebitosProps {
  data: CertidaoNegativaDebitosDto;
  includeDocument?: boolean;
  assinaturas?: { cargoNome?: string; pessoaNome?: string }[];
}

function PDFCertidaoNegativaDebitos({
  data,
  includeDocument = true,
  assinaturas,
}: PDFCertidaoNegativaDebitosProps) {
  const stylesCertidao = makePdfStyleSheet();

  const Wrapper = useMemo(
    () => (includeDocument ? Document : React.Fragment),
    [includeDocument]
  );

  const isAssociacao =
    ["associação", "associacao", "associacão"].some((t) =>
      data.dadosTenant.nome.toLowerCase().includes(t)
    ) || data.dadosTenant.tipoHabitacao === TenantTipoHabitacao.Comercial;

  const representanteTexto = isAssociacao ? "Diretor(a)" : "Síndico(a)";

  return (
    <Wrapper>
      <Page
        size="A4"
        style={stylesCertidao.page}
        wrap={false}
        orientation={"portrait"}
      >
        <View style={stylesCertidao.body}>
          {data.dadosTenant.logo ? (
            <Image
              src={makeImageSrcObject(data.dadosTenant.logo)}
              style={stylesCertidao.logo}
            />
          ) : null}

          <Text
            style={[
              stylesCertidao.title,
              stylesCertidao.fontBold,
              stylesCertidao.underlined,
            ]}
          >
            CERTIDÃO NEGATIVA DE DÉBITOS
          </Text>

          <Text style={[stylesCertidao.bodyText]}>
            O(a){" "}
            <Text style={[stylesCertidao.fontBold, stylesCertidao.uppercase]}>
              {data.dadosTenant.nome}
            </Text>
            , representado(a) pelo(a) seu(sua) {representanteTexto}, declara
            para os devidos fins de direito que a unidade{" "}
            {data.dadosUnidade.blocoQuadraNome ? (
              <Text>
                situada{" "}
                {data.dadosTenant.tipoEstrutura === "horizontal"
                  ? "na quadra"
                  : "no bloco"}{" "}
                <Text style={[stylesCertidao.fontBold]}>
                  {data.dadosUnidade.blocoQuadraNome}
                </Text>
                , número{" "}
                <Text style={[stylesCertidao.fontBold]}>
                  {data.dadosUnidade.numero}
                </Text>
              </Text>
            ) : (
              <Text>
                número{" "}
                <Text style={[stylesCertidao.fontBold]}>
                  {data.dadosUnidade.numero}
                </Text>
              </Text>
            )}
            , de titularidade cadastrada à{" "}
            <Text style={[stylesCertidao.fontBold]}>
              {data.dadosUnidade.responsavelNome}
            </Text>
            , está em dia com suas obrigações{" "}
            {isAssociacao ? "associativas" : "condominiais"}, não havendo
            débitos{" "}
            {data.dataInicio
              ? `durante o período de ${moment(data.dataInicio).format(
                  "DD/MM/YYYY"
                )} `
              : ""}
            até {moment(data.dataFim).format("DD/MM/YYYY")}.
          </Text>

          <Text style={[stylesCertidao.bodyText, { marginTop: "1cm" }]}>
            Por ser expressão da verdade, firma-se o presente.
          </Text>

          <Text
            style={[
              stylesCertidao.bodyText,
              { marginTop: "1cm", alignSelf: "center" },
            ]}
          >
            {data.dadosTenant.cidade}/{data.dadosTenant.uf},{" "}
            {moment().format("DD [de] MMMM [de] YYYY")}
          </Text>

          {assinaturas ? (
            <PDFAssinaturas
              assinaturas={assinaturas}
              fontSize={9}
              marginTop={64}
            />
          ) : null}

          <View style={stylesCertidao.footer}>
            <Text style={stylesCertidao.fontBold}>{data.dadosTenant.nome}</Text>
            {data.dadosAdministradoraTenant ? (
              <DadosAdministradora
                {...data.dadosAdministradoraTenant}
                endereco={data.dadosTenant.endereco}
              />
            ) : (
              <DadosTenant {...data.dadosTenant} />
            )}
          </View>
        </View>
      </Page>
    </Wrapper>
  );
}
export default PDFCertidaoNegativaDebitos;
